import React, { useState, useMemo, useEffect } from "react";
import styles from "./ticketMedioCursoTable.module.css";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import { formatNumberToCurrency } from "../../../utils/formatter";

// drill (opcional): { display, rows: [{ label, value|null }] } — troca a listagem de
// cursos pela listagem mês a mês do curso clicado no treemap. onClearDrill volta aos cursos.
const TicketMedioCursoTable = ({ disciplineData = [], showAll = false, drill = null, onClearDrill }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");

  const isDrill = !!(drill && drill.rows);

  useEffect(() => {
    setCurrentPage(1);
  }, [drill]);

  const validDisciplineData = useMemo(() => {
    return disciplineData
      .filter((discipline) =>
        (discipline.courseTitle || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const cmp = a.ticketMedio - b.ticketMedio;
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [disciplineData, searchTerm, sortOrder]);

  const activeRows = isDrill ? drill.rows : validDisciplineData;

  const totalPages = useMemo(
    () => Math.ceil(activeRows.length / itemsPerPage),
    [activeRows.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    if (showAll) return activeRows;
    const start = (currentPage - 1) * itemsPerPage;
    return activeRows.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, activeRows, showAll]);

  const handlePageClick = (page) => setCurrentPage(page);

  const emptyRows = showAll ? [] : Array(Math.max(0, itemsPerPage - currentTableData.length)).fill(null);
  const hasNoData = activeRows.length === 0;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>
            {isDrill ? `Ticket Médio por Mês — ${drill.display}` : "Ticket Médio por Curso"}
          </h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div className={styles.popover}>
                <p style={{ margin: 0, fontSize: "12px" }}>
                  {isDrill
                    ? "Ticket médio mês a mês do curso selecionado no treemap, dentro do período filtrado. Meses sem recebimento aparecem com '—'."
                    : "Tabela que exibe o ticket médio por curso, permitindo visualizar quais cursos geram maior receita média por aluno. Os dados podem ser filtrados por nome do curso e ordenados por valor."}
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  className={styles.closeBtn}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          {isDrill ? (
            <button
              onClick={onClearDrill}
              style={{
                border: "1px solid #0962AD",
                color: "#0962AD",
                background: "#fff",
                borderRadius: "8px",
                padding: "8px 14px",
                cursor: "pointer",
                fontWeight: 600
              }}
            >
              ← Voltar aos cursos
            </button>
          ) : (
            <>
              <div className={styles.searchWrapper}>
                <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Pesquisar curso..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.searchInput}
                />
              </div>

              <select
                className={styles.orderWrapper}
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="desc">Maior Ticket Médio</option>
                <option value="asc">Menor Ticket Médio</option>
              </select>
            </>
          )}
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{isDrill ? "Mês" : "Título do Curso"}</th>
            <th>Ticket Médio</th>
          </tr>
        </thead>
        <tbody>
          {hasNoData ? (
            <tr>
              <td colSpan="2" className={styles.noData}>
                {isDrill ? "Sem meses no período" : "Nenhum curso encontrado"}
              </td>
            </tr>
          ) : (
            <>
              {currentTableData.map((row, idx) => (
                <tr key={idx}>
                  <td>{isDrill ? row.label : row.courseTitle}</td>
                  <td>
                    {isDrill
                      ? (row.value !== null ? formatNumberToCurrency(row.value) : "—")
                      : formatNumberToCurrency(row.ticketMedio)}
                  </td>
                </tr>
              ))}
              {emptyRows.map((_, idx) => (
                <tr key={`empty-${idx}`}>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {!showAll && totalPages > 1 && (
        <div className={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={page === currentPage ? styles.activePage : ""}
              onClick={() => handlePageClick(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TicketMedioCursoTable;
